import React, { useEffect, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { t } from "i18next";
import { useSelector } from "react-redux";
import {
  AssetSource,
  AssetType,
  IAsset,
  IUrlEmbedCheck,
  LoadingStatus,
  NendaProduct,
} from "../../../../../../../types/NendaTypes";
import { CustomerPortalState, store } from "../../../../store";
import {
  CreateCompanyAsset,
  CreatePremiseAsset,
} from "../../../../store/reducers/assetReducer";
import {
  ResetUploadStatus,
  UploadFileAndCreateAsset,
  getAssetUploadStatus,
} from "../../../../store/reducers/companyReducer";
import TextInput from "../../../../ui-components/input/TextInput";
import { FileUpload } from "../../../FileUpload";
import UrlEmbedBadge from "../../../AssetLibrary/UrlEmbedBadge";
import constants from "@shared/constants";
import { isUrl } from "@shared/urlUtils";
import { companyService } from "../../../../../../http/companyService";
import { selectNavigatedPremise } from "../../../../store/reducers/organizationUnitReducer";
import { FileUploadOutlined, LinkOutlined } from "@mui/icons-material";

const style = {
  textField: {
    maxWidth: "50ch",
    width: "100%",
  },
};

export const VALID_ASSET_FILE_TYPES = [
  "video/mp4",
  "video/avi",
  "image/png",
  "image/jpeg",
];

export const PROMOTION_UPLOAD_REQUEST_ID = "promotion";

type AssetOperation = "upload" | "create";

interface UrlCheckResult {
  embedCheck: IUrlEmbedCheck;
}

interface CreateAssetFormProps {
  named?: boolean;
  submitText?: string;
  description?: string;
  requestId?: string;
  companyId: string;
  premiseId?: string;
  onClose: () => void;
}

export const CreateAssetForm: React.FC<CreateAssetFormProps> = ({
  named = true,
  submitText,
  description,
  requestId = PROMOTION_UPLOAD_REQUEST_ID,
  companyId,
  premiseId,
  onClose,
}) => {
  const [operation, setOperation] = useState<AssetOperation>("upload");
  const [name, setName] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState<UrlCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const status = useSelector((state: CustomerPortalState) =>
    getAssetUploadStatus(state, requestId)
  );
  const navigatedPremise = useSelector(selectNavigatedPremise);
  // Web pages can only be created on a premise that has been switched on for
  // them: not every screen can render one, and a company-wide URL asset could
  // not be honoured at every premise. The server enforces both rules.
  const canCreateUrlAssets =
    !!premiseId && !!navigatedPremise?.signageConfig?.areUrlAssetsEnabled;
  const nameValidated = named ? !!name : true;
  const isUrlValid = isUrl(url);

  const validateInput = () =>
    operation === "upload"
      ? nameValidated && files && files.length > 0
      : nameValidated && isUrlValid;

  const reset = () => {
    setName("");
    setOperation("upload");
    setFiles([]);
    setUrl("");
    setPreview(null);
  };

  useEffect(() => {
    if (!canCreateUrlAssets && operation === "create") {
      setOperation("upload");
      setUrl("");
      setPreview(null);
    }
  }, [canCreateUrlAssets, operation]);

  useEffect(() => {
    if (status === "succeeded") {
      reset();
      onClose();
      store.dispatch(ResetUploadStatus(requestId));
    }
  }, [status]);

  const handleOperationChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: AssetOperation | null
  ) => {
    if (!value) return;
    // Drop the other mode's input so a stale file or URL can never be
    // submitted from a mode the user has left.
    setFiles([]);
    setUrl("");
    setPreview(null);
    setOperation(value);
  };

  const assetTypeFromFile = (file: File): AssetType | undefined => {
    if (!file) {
      return undefined;
    }
    const typeCategory = file.type.split("/")[0];
    switch (typeCategory) {
      case "image":
        return AssetType.IMAGE;
      case "video":
        return AssetType.VIDEO;
      default:
        return undefined;
    }
  };

  /**
   * Asks the server whether the page allows being shown inside an iframe.
   * Resolves to null on failure: a failed check must never stop someone
   * creating the asset.
   */
  const runUrlCheck = (target: string): Promise<UrlCheckResult | null> =>
    new Promise((resolve) => {
      companyService.validateAssetUrl(companyId, target).subscribe({
        next: (response: any) => resolve(response as UrlCheckResult),
        error: () => resolve(null),
      });
    });

  const checkUrl = async () => {
    if (!isUrl(url) || isChecking) return;
    setIsChecking(true);
    try {
      setPreview(await runUrlCheck(url));
    } finally {
      setIsChecking(false);
    }
  };

  const uploadAsset = () => {
    const assetType = assetTypeFromFile(files[0]);
    if (!assetType) return;

    store.dispatch(
      UploadFileAndCreateAsset({
        companyId,
        file: files[0],
        scope: NendaProduct.SIGNAGE,
        assetType,
        assetName: name,
        premiseId,
        requestId,
      })
    );
  };

  const createUrlAsset = async () => {
    setIsSaving(true);
    try {
      // Check now if the field never lost focus, so an asset is never stored
      // without a verdict.
      const result = preview ?? (await runUrlCheck(url));

      const asset = {
        name,
        locator: url,
        type: AssetType.URL,
        source: AssetSource.EXTERNAL,
        product: NendaProduct.SIGNAGE,
        company: companyId,
        ...(premiseId ? { premise: premiseId } : {}),
        embedCheck: result?.embedCheck,
      };

      // The reducer closes the dialog on CREATE_*_ASSET_SUCCESS, so this path
      // deliberately does not call onClose() itself.
      store.dispatch(
        premiseId
          ? CreatePremiseAsset(premiseId, asset)
          : CreateCompanyAsset(companyId, asset as IAsset)
      );
      reset();
    } finally {
      setIsSaving(false);
    }
  };

  const createAsset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (operation === "upload") {
      uploadAsset();
    } else {
      void createUrlAsset();
    }
  };

  const { SIGNAGE_SLOT_DURATION: maxDuration } = constants.Signage();
  const minDuration = 5;
  const showUrlInvalid = operation === "create" && !!url && !isUrlValid;

  return (
    <Box sx={{ width: "100%", my: "1rem" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          alignItems: "center",
          width: "100%",
        }}
      >
        {canCreateUrlAssets && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={operation}
            onChange={handleOperationChange}
          >
            <ToggleButton value="upload">
              <FileUploadOutlined sx={{ mr: "0.4rem", fontSize: "1rem" }} />
              {t("customerportal.pages.assets.create.mode_upload")}
            </ToggleButton>
            <ToggleButton value="create">
              <LinkOutlined sx={{ mr: "0.4rem", fontSize: "1rem" }} />
              {t("customerportal.pages.assets.create.mode_link")}
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {named && (
          <TextInput
            sx={style.textField}
            id="name"
            label="Name"
            variant="standard"
            value={name}
            autoComplete="off"
            required
            onChange={(e) => setName(e.target.value)}
          />
        )}
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Box>
            {operation === "upload" && (
              <FileUpload
                files={files}
                onFileChange={setFiles}
                allowedFileTypes={VALID_ASSET_FILE_TYPES}
              />
            )}
            {operation === "create" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  alignItems: "center",
                }}
              >
                <TextInput
                  sx={style.textField}
                  id="url"
                  label={t("customerportal.pages.assets.create.url_label")}
                  variant="standard"
                  value={url}
                  autoComplete="off"
                  required
                  placeholder="https://example.com"
                  error={showUrlInvalid}
                  helperText={
                    showUrlInvalid
                      ? t("customerportal.pages.assets.create.url_invalid")
                      : undefined
                  }
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setPreview(null);
                  }}
                  onBlur={checkUrl}
                />

                {isChecking && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <CircularProgress size="1rem" />
                    <Typography variant="caption" color="text.primary">
                      {t("customerportal.pages.assets.create.checking_url")}
                    </Typography>
                  </Box>
                )}

                {!isChecking && preview && (
                  <UrlEmbedBadge
                    embedCheck={preview.embedCheck}
                    showWhenEmbeddable
                  />
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography variant={"caption"}>
              {description ||
                t("customerportal.pages.promotions.video_duration", {
                  minDuration,
                  maxDuration,
                })}
            </Typography>
          </Box>
        </Stack>

        <LoadingButton
          sx={{ mt: 1, width: isMobile ? "100%" : undefined }}
          disabled={!validateInput()}
          onClick={createAsset}
          loading={status === LoadingStatus.LOADING || isSaving || isChecking}
          variant="contained"
          endIcon={
            operation === "upload" ? <FileUploadOutlined /> : <LinkOutlined />
          }
        >
          {submitText || t("common.create")}
        </LoadingButton>
      </Box>
    </Box>
  );
};
